import { SignatureInsertWithRows, SignatureUpdate } from '../common/types/api.js'
import { httpDelete, httpGet, httpSend } from './http-utils.js'
import { Api, State } from './types.js'

export const createApi = (state: State): Api => ({
    eat: {
        getStats: async () => httpGet(state, '/eat/stats'),
        getMyOrders: async () => httpGet(state, '/eat/orders/mine'),
        getMeals: async () => {
            const res = await httpGet<{ data: import('../common/types/api.js').EatMealsData }>(
                state,
                '/eat/meals',
            )
            return res.data
        },
        getSettings: async () => {
            const res = await httpGet<{ data: import('../common/types/api.js').EatSettings }>(
                state,
                '/eat/settings',
            )
            return res.data
        },
        createOrder: async (mealId, commentId) => {
            const res = await httpSend(state, 'POST', '/eat/orders', {
                meal_id: mealId,
                comment_id: commentId ?? undefined,
                user_id: state.user?.id ?? 0,
            })
            const body = (await res.json()) as { data: import('../common/types/api.js').EatOrder }
            return body.data
        },
        deleteOrder: async (orderId) => {
            await httpDelete(state, `/eat/orders/${orderId}`)
        },
        pay: async (userId) => {
            await httpSend(state, 'POST', `/eat/users/${userId}/pay`, {})
        },
    },
    signatures: {
        getSignatures: async () => httpGet(state, '/signaturetool/signatures'),
        createSignature: async (data: SignatureInsertWithRows) => {
            await httpSend(state, 'POST', '/signaturetool/signatures', data)
        },
        updateSignature: async (id: string, data: SignatureUpdate) => {
            await httpSend(state, 'PUT', `/signaturetool/signatures/${id}`, data)
        },
        deleteSignature: async (id: string) => {
            await httpDelete(state, `/signaturetool/signatures/${id}`)
        },
    },
    me: {
        getProfile: async () => {
            const res = await httpGet<{ data: import('../common/types/profile.js').Profile }>(
                state,
                '/me/profile',
            )
            return res.data
        },
    },
    playtime: {
        getProfile: async () => httpGet(state, '/playtime/profile'),
        getUserProfile: async (userId) => httpGet(state, `/playtime/profile/${userId}`),
        getLeaderboard: async () => httpGet(state, '/playtime/leaderboard'),
        getBadges: async () => httpGet(state, '/playtime/badges'),
        createBadgeRequest: async (data) => {
            await httpSend(state, 'POST', '/playtime/badge-requests', data)
        },
        acceptBadgeRequest: async (requestId) => {
            await httpSend(state, 'POST', `/playtime/badge-requests/${requestId}/accept`, {})
        },
        markBadgeSeen: async (badgeId) => {
            await httpSend(state, 'POST', `/playtime/badges/${badgeId}/seen`, {})
        },
        getCampaign: async (id) => httpGet(state, `/playtime/campaigns/${id}`),
        getForm: async (id) => httpGet(state, `/playtime/forms/${id}`),
        getCampaignStatus: async (id: number) => httpGet(state, `/playtime/campaigns/${id}/status`),
        getUnansweredCampaign: async () => httpGet(state, '/playtime/campaigns/unanswered'),
        getActiveCampaign: async () => httpGet(state, '/playtime/campaigns/active'),
        submitSurvey: async (campaignId, answers) => {
            const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
                question_id: Number(questionId),
                answer,
            }))
            await httpSend(state, 'POST', `/playtime/campaigns/${campaignId}/submit`, {
                answers: answersArray,
            })
        },
        updateRoadmap: async (groupId, status) => {
            await httpSend(state, 'POST', '/playtime/roadmap/update', {
                groupId,
                status,
            })
        },
        getRoadmapStatus: async (groupId) =>
            httpGet(
                state,
                groupId
                    ? `/playtime/roadmap/status?groupId=${groupId}`
                    : '/playtime/roadmap/status',
            ),
        getBalanceTime: async (params) => {
            const searchParams = new URLSearchParams(params ?? {})
            return httpGet(state, `/balancetime?${searchParams.toString()}`)
        },
        getBalanceTimeAuthStatus: async () => httpGet(state, '/balancetime/auth-status'),
        saveHarvestCallback: async (code) => {
            await httpSend(state, 'POST', '/balancetime/harvest-callback', { code })
        },
    },
    portal: {
        getLinks: async () => httpGet(state, '/portal-links'),
        getMyLeaveBalance: async () => httpGet(state, '/portal/me/leave-balance'),
    },
    urlShortener: {
        getLinks: async () => httpGet(state, '/url-links'),
        getLink: async (id: number) => httpGet(state, `/url-links/${id}`),
        createLink: async (data) => {
            const res = await httpSend(state, 'POST', '/url-links', data)
            return res.json() as ReturnType<Api['urlShortener']['createLink']>
        },
        deleteLink: async (id: number) => {
            await httpDelete(state, `/url-links/${id}`)
        },
        saveQrSettings: async (id: number, settings) => {
            const res = await httpSend(state, 'PUT', `/url-links/${id}/qr`, settings)
            return res.json() as ReturnType<Api['urlShortener']['saveQrSettings']>
        },
        getAccessLogs: async (id: number, page = 1) =>
            httpGet(state, `/url-links/${id}/access-logs?page=${page}`),
    },
})
